import { useCallback, useState, useRef, useEffect } from "react";
import api from "../../services/api";

const CACHE_CONFIG = {
  expiryTime: 300000,
  cleanupInterval: 900000,
  maxSize: 50,
};

const globalCache = {
  dashboard: new Map(),
  extendedMetrics: new Map(),
  lastCleanup: Date.now(),
  pendingRequests: new Map(),
};

const cleanupCache = () => {
  const now = Date.now();

  if (now - globalCache.lastCleanup > CACHE_CONFIG.cleanupInterval) {
    globalCache.dashboard.forEach((entry, key) => {
      if (now - entry.timestamp > CACHE_CONFIG.expiryTime) {
        globalCache.dashboard.delete(key);
      }
    });

    globalCache.extendedMetrics.forEach((entry, key) => {
      if (now - entry.timestamp > CACHE_CONFIG.expiryTime) {
        globalCache.extendedMetrics.delete(key);
      }
    });

    if (globalCache.dashboard.size > CACHE_CONFIG.maxSize) {
      const keysToDelete = [...globalCache.dashboard.entries()]
        .sort((a, b) => a[1].timestamp - b[1].timestamp)
        .slice(0, globalCache.dashboard.size - CACHE_CONFIG.maxSize)
        .map((entry) => entry[0]);

      keysToDelete.forEach((key) => globalCache.dashboard.delete(key));
    }

    if (globalCache.extendedMetrics.size > CACHE_CONFIG.maxSize) {
      const keysToDelete = [...globalCache.extendedMetrics.entries()]
        .sort((a, b) => a[1].timestamp - b[1].timestamp)
        .slice(0, globalCache.extendedMetrics.size - CACHE_CONFIG.maxSize)
        .map((entry) => entry[0]);

      keysToDelete.forEach((key) => globalCache.extendedMetrics.delete(key));
    }

    globalCache.lastCleanup = now;
  }
};

const generateCacheKey = (params) => {
  return JSON.stringify(params || {});
};

const deduplicateRequest = async (cacheKey, requestFn) => {
  if (globalCache.pendingRequests.has(cacheKey)) {
    return globalCache.pendingRequests.get(cacheKey);
  }

  const promise = requestFn().finally(() => {
    globalCache.pendingRequests.delete(cacheKey);
  });

  globalCache.pendingRequests.set(cacheKey, promise);

  return promise;
};

const useDashboard = () => {
  const [loading, setLoading] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const safeSetLoading = useCallback((value) => {
    if (isMountedRef.current) {
      setLoading(value);
    }
  }, []);

  const find = useCallback(
    async (params, options = {}) => {
      safeSetLoading(true);
      try {
        const cacheKey = generateCacheKey(params);
        cleanupCache();

        if (!options.skipCache) {
          const cachedData = globalCache.dashboard.get(cacheKey);
          if (
            cachedData &&
            Date.now() - cachedData.timestamp < CACHE_CONFIG.expiryTime
          ) {
            return cachedData.data;
          }
        }

        const requestFn = () =>
          api.request({
            url: `/dashboard`,
            method: "GET",
            params,
          });

        const response = await deduplicateRequest(cacheKey, requestFn);
        const data = response.data;

        globalCache.dashboard.set(cacheKey, {
          data,
          timestamp: Date.now(),
        });

        return data;
      } finally {
        safeSetLoading(false);
      }
    },
    [safeSetLoading]
  );

  const findWithExtendedMetrics = useCallback(
    async (params, options = {}) => {
      safeSetLoading(true);
      try {
        const cacheKey = generateCacheKey({
          ...params,
          includeExtendedMetrics: true,
        });

        cleanupCache();

        if (!options.skipCache) {
          const cachedData = globalCache.dashboard.get(cacheKey);
          if (
            cachedData &&
            Date.now() - cachedData.timestamp < CACHE_CONFIG.expiryTime
          ) {
            return cachedData.data;
          }
        }

        const requestFn = () =>
          api.request({
            url: `/dashboard`,
            method: "GET",
            params: {
              ...params,
              includeExtendedMetrics: true,
            },
          });

        const response = await deduplicateRequest(cacheKey, requestFn);
        const data = response.data || { counters: {}, attendants: [], extendedMetrics: null };

        globalCache.dashboard.set(cacheKey, {
          data,
          timestamp: Date.now(),
        });

        return data;
      } catch (error) {
        console.error("Erro ao buscar dados do dashboard:", error);
        // Retornar dados padrão em caso de erro
        return { counters: {}, attendants: [], extendedMetrics: null };
      } finally {
        safeSetLoading(false);
      }
    },
    [safeSetLoading]
  );

  const getExtendedMetrics = useCallback(
    async (params) => {
      safeSetLoading(true);
      try {
        const cacheKey = generateCacheKey(params);
        cleanupCache();

        const cachedData = globalCache.extendedMetrics.get(cacheKey);
        if (
          cachedData &&
          Date.now() - cachedData.timestamp < CACHE_CONFIG.expiryTime
        ) {
          return cachedData.data;
        }

        const requestFn = () =>
          api.request({
            url: `/dashboard/extended-metrics`,
            method: "GET",
            params,
          });

        const response = await deduplicateRequest(cacheKey, requestFn);
        const data = response.data;

        globalCache.extendedMetrics.set(cacheKey, {
          data,
          timestamp: Date.now(),
        });

        return data;
      } catch (error) {
        // Se a rota não existe (404), retornar dados vazios silenciosamente
        if (error?.response?.status === 404) {
          console.log("Rota extended-metrics não disponível, usando dados vazios");
          return null;
        }
        throw error;
      } finally {
        safeSetLoading(false);
      }
    },
    [safeSetLoading]
  );

  const getReport = useCallback(
    async (params) => {
      safeSetLoading(true);
      try {
        const cacheKey = `report_${generateCacheKey(params)}`;

        const requestFn = () =>
          api.request({
            url: `/ticketreport/reports`,
            method: "GET",
            params,
          });

        const response = await deduplicateRequest(cacheKey, requestFn);
        return response.data;
      } finally {
        safeSetLoading(false);
      }
    },
    [safeSetLoading]
  );

  const preloadDashboardData = useCallback((params) => {
    const cacheKey = generateCacheKey({
      ...params,
      includeExtendedMetrics: true,
    });

    if (
      globalCache.dashboard.has(cacheKey) ||
      globalCache.pendingRequests.has(cacheKey)
    ) {
      return;
    }

    const requestFn = () =>
      api.request({
        url: `/dashboard`,
        method: "GET",
        params: {
          ...params,
          includeExtendedMetrics: true,
        },
      });

    deduplicateRequest(cacheKey, requestFn)
      .then((response) => {
        globalCache.dashboard.set(cacheKey, {
          data: response.data,
          timestamp: Date.now(),
        });
      })
      .catch((err) => {
        console.error("Erro ao pré-carregar dados do dashboard:", err);
      });
  }, []);

  const invalidateCache = useCallback((specificKey = null) => {
    if (specificKey) {
      globalCache.dashboard.delete(specificKey);
      globalCache.extendedMetrics.delete(specificKey);
    } else {
      globalCache.dashboard.clear();
      globalCache.extendedMetrics.clear();
    }
  }, []);

  const limitCacheSize = useCallback((maxSize = 10) => {
    if (globalCache.dashboard.size > maxSize) {
      const keysToDelete = [...globalCache.dashboard.entries()]
        .sort((a, b) => a[1].timestamp - b[1].timestamp)
        .slice(0, globalCache.dashboard.size - maxSize)
        .map((entry) => entry[0]);

      keysToDelete.forEach((key) => globalCache.dashboard.delete(key));
    }

    if (globalCache.extendedMetrics.size > maxSize) {
      const keysToDelete = [...globalCache.extendedMetrics.entries()]
        .sort((a, b) => a[1].timestamp - b[1].timestamp)
        .slice(0, globalCache.extendedMetrics.size - maxSize)
        .map((entry) => entry[0]);

      keysToDelete.forEach((key) => globalCache.extendedMetrics.delete(key));
    }
  }, []);

  const clearCache = invalidateCache;

  const forceRefresh = useCallback(
    async (params) => {
      safeSetLoading(true);
      try {
        const cacheKey = generateCacheKey({
          ...params,
          includeExtendedMetrics: true,
        });

        invalidateCache(cacheKey);

        const requestFn = () =>
          api.request({
            url: `/dashboard`,
            method: "GET",
            params: {
              ...params,
              includeExtendedMetrics: true,
            },
          });

        const response = await deduplicateRequest(cacheKey, requestFn);
        const data = response.data;

        globalCache.dashboard.set(cacheKey, {
          data,
          timestamp: Date.now(),
        });

        return data;
      } catch (error) {
        throw error;
      } finally {
        safeSetLoading(false);
      }
    },
    [safeSetLoading, invalidateCache]
  );

  return {
    find,
    findWithExtendedMetrics,
    getExtendedMetrics,
    getReport,
    loading,
    preloadDashboardData,
    invalidateCache,
    clearCache,
    limitCacheSize,
    forceRefresh,
  };
};

export default useDashboard;
